import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, TextField, Link, Alert, CircularProgress,
  InputAdornment,
} from '@mui/material'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import { FancyButton } from '../comps/FancyButtons'
import { buildCoreUrl, buildCoreApiUrl, getIp } from '../common/common'

const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30

function setCookie(name: string, value: string) {
  const hostname = window.location.hostname.toLowerCase()
  const useSharedDomain = hostname === 'contractfinderpro.com' || hostname.endsWith('.contractfinderpro.com')
  const parts = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax', `Max-Age=${AUTH_COOKIE_MAX_AGE}`]
  if (useSharedDomain) parts.push('Domain=.contractfinderpro.com')
  if (window.location.protocol === 'https:') parts.push('Secure')
  document.cookie = parts.join('; ')
}

export default function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const forgotPasswordUrl = buildCoreUrl('/forgotpassword.aspx')

  async function submitLogin() {
    setError('')
    setIsLoggingIn(true)
    try {
      const ip = await getIp()
      const body = new URLSearchParams({
        username: email,
        password,
        grant_type: 'password',
        ip,
        url: window.location.href,
      })
      const res = await fetch(buildCoreApiUrl('api/token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })
      const data = await res.json()
      if (!res.ok || !data.access_token) {
        setError('Login failed. Please check your email and password.')
        return
      }
      setCookie('cfp_access_token', data.access_token)
      if (data.refresh_token) setCookie('cfp_refresh_token', data.refresh_token)
      localStorage.setItem('access_token', data.access_token)
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
      navigate('/app')
    } catch {
      setError('Unable to connect. Please try again.')
    } finally {
      setIsLoggingIn(false)
    }
  }

  function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    submitLogin()
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Box sx={{ width: '100%', maxWidth: 380, px: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
          Please Log in
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Box component="form" onSubmit={handleLogin} autoComplete="on">
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 0.5, opacity: 0.75 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="email"
              id="login_email"
              placeholder="Enter Email Address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon fontSize="small" sx={{ opacity: 0.45 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.75 }}>Password</Typography>
              <Link href={forgotPasswordUrl} variant="caption" tabIndex={-1} underline="hover" sx={{ opacity: 0.65 }}>
                Forgot Password?
              </Link>
            </Box>
            <TextField
              fullWidth
              size="small"
              type="password"
              id="login_password"
              placeholder="Enter Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyOutlinedIcon fontSize="small" sx={{ opacity: 0.45 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FancyButton
              solid
              disabled={isLoggingIn}
              fullWidth
              onClick={submitLogin}
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
              {isLoggingIn && <CircularProgress size={18} sx={{ flexShrink: 0 }} />}
            </FancyButton>

          </Box>
        </Box>
      </Box>
    </Box>
  )
}
